import React, { useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import { useSelector, shallowEqual, useDispatch } from 'react-redux';
import { createPortal } from 'react-dom';
import { useParams } from 'react-router-dom';
import { Dropdown } from 'react-bootstrap';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { FilterMatchMode, FilterService } from 'primereact/api';
import classNames from 'classnames';
import { user } from '../../../redux/user';
import KebobIcon from '../../../assets/images/more-horizontal.svg';
import BulkActionIcon from '../../../assets/images/bounding-box.svg';
import BulkActionWhiteIcon from '../../../assets/images/bounding-box-white.svg';
import filterIcon from '../../../assets/images/filters.svg';
import filterArrow from '../../../assets/images/filter-arrow.svg';
import arrow from '../../../assets/images/arrow-white.svg';
import { showNotification } from '../../../redux/notification';
import {
	useFlexFacilityProfessionalsQuery,
	useUpdateFlexFacilityProfessionalsStatusMutation,
	useBulkUpdateFlexFacilityProfessionalsStatusMutation
} from '../../../redux/flex-facility-professionals';
import utils from '../../../utils/utils';
import MultiSelectSearch from '../../../components/MultiSelectSearch/MultiSelectSearch';
import TextSearchComponent from '../../../components/SearchComponent/TextSearchComponent';
import LocationsStatusUpdateConfirmation from '../professionals/locations/LocationsStatusUpdateConfirmation';
import LocationsBulkStatusUpdateConfirmation from '../professionals/locations/LocationsBulkStatusUpdateConfirmation';
import IsAuthorizedForManagementCompany from '../../../components/IsAuthorizedForManagementCompany/IsAuthorizedForManagementCompany';
import DownloadIcon from '../../../assets/images/download.svg';
import { PERMISSION } from '../../../constants';

import './FlexFacilityProfessionals.scss';

const WORKER_STATUSES = [
	{ label: 'Enabled', value: 'enabled', checked: true },
	{ label: 'Pending', value: 'pending', checked: true },
	{ label: 'Disabled', value: 'disabled', checked: true },
	{ label: 'Terminated', value: 'terminated', checked: true },
	{ label: 'Deactivated', value: 'deactivated', checked: true }
];

const LOCATION_STATUSES = [
	{ label: 'Enabled', value: 'Enabled', checked: true },
	{ label: 'Disabled', value: 'Disabled', checked: true },
	{ label: '', value: '', labelSuffix: 'Empty', checked: true }
];

const DEFAULT_FILTER_OPTIONS = {
	worker_status: WORKER_STATUSES,
	locationStatus: LOCATION_STATUSES,
	resourceType: [],
	specialtiesIds: []
};

const DEFAULT_FILTERS = { global: { value: '', matchMode: FilterMatchMode.CONTAINS } };

const FlexFacilityProfessionals = () => {
	const searchBoxTargetDiv = document.getElementById('searchbox-placeholder-target');
	const downloadButtonTargetDiv = document.getElementById('btn-download-target');

	const timer = useRef(null);

	const { facilityId, managementCompanyId } = useParams();
	const dispatch = useDispatch();
	const userData = useSelector(user, shallowEqual);
	const [updateAnimation, setUpdateAnimation] = useState(false);
	const [bulkUpdateActive, setBulkUpdateActive] = useState(false);
	const [selectedProfessionals, setSelectedProfessionals] = useState([]);
	const [selectedAction, setSelectedAction] = useState(null);
	const [selectedBulkAction, setSelectedBulkAction] = useState(null);
	const [showBulkConfirmationModal, setShowBulkConfirmationModal] = useState(false);
	const [isFiltersSectionOpen, setIsFiltersSectionOpen] = useState(true);
	const [filterOptions, setFilterOptions] = useState(DEFAULT_FILTER_OPTIONS);
	const [filters, setFilters] = useState(DEFAULT_FILTERS);
	const [showBulkMenu, setShowBulkMenu] = useState(true);
	const [showConfirmationModal, setShowConfirmationModal] = useState(false);

	const dataTableRef = useRef(null);

	const filterCount =
		(filters.worker_status?.value?.length ? 1 : 0) +
		(filters.locationStatus?.value?.length ? 1 : 0) +
		(filters.resourceType?.value?.length ? 1 : 0) +
		(filters.specialtiesIds?.value?.length ? 1 : 0);
	const searchTerm = filters.global?.value || '';

	const facility = useMemo(() => userData.facilitiesEnabled.find((_facility) => _facility.id === facilityId), [
		userData,
		facilityId
	]);
	const { data: rawProfessionals, isLoading, error, isError } = useFlexFacilityProfessionalsQuery(
		{ managementCompanyId, facilityId },
		{
			refetchOnMountOrArgChange: true
		}
	);
	const [
		updateFlexFacilityProfessionalsStatus,
		{ isLoading: loadingSingleUpdate }
	] = useUpdateFlexFacilityProfessionalsStatusMutation();

	const [
		bulkUpdateFlexFacilityProfessionalsStatusMutation,
		{ isLoading: loadingBulkUpdate }
	] = useBulkUpdateFlexFacilityProfessionalsStatusMutation();

	const loadingUpdate = loadingSingleUpdate || loadingBulkUpdate;

	useEffect(() => {
		return () => clearTimeout(timer.current);
	}, []);

	const professionals = useMemo(
		() =>
			rawProfessionals?.map((item) => {
				const sortedResourceTypes = item.resource_types?.length
					? [...item.resource_types].sort((a, b) => b.rank - a.rank)
					: null;
				const resourceTypeString = sortedResourceTypes?.map((type) => type.code).join(', ') || '-';

				const augmentedSpecialties = [];
				sortedResourceTypes?.forEach((type) => {
					const isCurrentResourceRT = ['CRT', 'RRT'].includes(type.code);

					const specialtiesForResourceType = item.specialties
						?.filter(
							(specialty) =>
								specialty.resource_type_id === type.id ||
								(['CRT', 'RRT'].includes(type.code) && specialty.code.startsWith('RT_'))
						)
						.sort((a, b) => a.short_name.localeCompare(b.short_name));
					if (specialtiesForResourceType?.length > 0) {
						augmentedSpecialties.push(
							`${isCurrentResourceRT ? 'RT' : type.code} - ${specialtiesForResourceType
								.map((spec) => `${spec.short_name}`)
								.join(', ')}`
						);
					}
				});

				const uniqueSpecialties = [...new Set(augmentedSpecialties)];
				const specialtiesString = uniqueSpecialties?.length ? uniqueSpecialties.join('; ') : '-';

				return {
					...item,
					resourceTypeString,
					specialtiesString,
					resourceType: item.resource_types?.map((rt) => rt.code),
					specialtiesIds: item.specialties?.map((spec) => spec.id),
					/* eslint-disable-next-line no-nested-ternary */
					locationStatus: ['deactivated', 'terminated'].includes(item.worker_status)
						? ''
						: item.is_location_enabled
						? 'Enabled'
						: 'Disabled'
				};
			}),
		[rawProfessionals]
	);

	const setDefaultFilterOptions = (activeFilters = filters) => {
		const resourceTypesMap = new Map();
		const specialtiesMap = new Map();

		const isChecked = (filterType, value) => {
			const activeValues = activeFilters[filterType]?.value;
			return !activeValues || activeValues.includes(value);
		};

		rawProfessionals.forEach((worker) => {
			let rtTypeLicenseId = null;
			worker.resource_types?.forEach((rt) => {
				if (['RRT', 'CRT'].includes(rt.code)) {
					rtTypeLicenseId = rt.id;
				}
				if (!resourceTypesMap.has(rt.id)) {
					resourceTypesMap.set(rt.id, rt);
				}
			});
			worker.specialties?.forEach((spec) => {
				if (!specialtiesMap.has(spec.id)) {
					if (rtTypeLicenseId && spec.code === 'RT_PEDIATRIC_PEDS') {
						specialtiesMap.set(spec.id, { ...spec, resource_type_id: rtTypeLicenseId });
					} else {
						specialtiesMap.set(spec.id, spec);
					}
				}
			});
		});

		const uniqueResourceTypesList = Array.from(resourceTypesMap.values())
			.sort((a, b) => b.rank - a.rank)
			.map(({ code }) => ({
				label: code,
				value: code,
				checked: isChecked('resourceType', code)
			}));
		const uniqueSpecialtiesList = Array.from(specialtiesMap.values()).map((spec) => {
			const matchingResourceType = resourceTypesMap.get(spec.resource_type_id);
			const isRtLicense = ['RRT', 'CRT'].includes(matchingResourceType.code);

			return {
				label: `${isRtLicense ? 'RT' : matchingResourceType.code} - ${spec.short_name}`,
				value: spec.id,
				checked: isChecked('specialtiesIds', spec.id)
			};
		});
		uniqueSpecialtiesList.push({
			label: 'None',
			value: 'NONE',
			checked: isChecked('specialtiesIds', 'NONE')
		});

		setFilterOptions({
			worker_status: WORKER_STATUSES.map((option) => ({
				...option,
				checked: isChecked('worker_status', option.value)
			})),
			locationStatus: LOCATION_STATUSES.map((option) => ({
				...option,
				checked: isChecked('locationStatus', option.value)
			})),
			resourceType: uniqueResourceTypesList,
			specialtiesIds: uniqueSpecialtiesList
		});
	};

	useEffect(() => {
		if (rawProfessionals) {
			setDefaultFilterOptions();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [rawProfessionals]);

	const handleMultiSelectFiltersApply = (filterType, options) => {
		setFilterOptions({ ...filterOptions, [filterType]: options });
		const newSelection = options.filter((option) => option.checked).map((option) => option.value);
		if (newSelection.length === options.length) {
			// eslint-disable-next-line no-unused-vars
			const { [filterType]: removedFilter, ...rest } = filters;
			setFilters(rest);
		} else {
			setFilters((prev) => {
				return {
					...prev,
					[filterType]: { ...(prev[filterType] || {}), value: newSelection, matchMode: FilterMatchMode.CUSTOM }
				};
			});
		}
	};

	const handleClearFilters = () => {
		const clearedFilters = { global: { value: filters.global.value, matchMode: FilterMatchMode.CONTAINS } };
		setFilters(clearedFilters);
		setDefaultFilterOptions(clearedFilters);
	};

	FilterService.register(`custom_worker_status`, (item, filter) => {
		return filter.indexOf(item.toLowerCase()) > -1;
	});
	FilterService.register(`custom_locationStatus`, (item, filter) => {
		return filter.indexOf(item) > -1;
	});
	FilterService.register(`custom_resourceType`, (items, filter) => {
		const itemsSet = new Set(items);
		return filter.some((item) => itemsSet.has(item));
	});
	FilterService.register(`custom_specialtiesIds`, (items, filter) => {
		const itemsSet = new Set(items);
		return filter.some((item) => itemsSet.has(item)) || (filter.includes('NONE') && itemsSet.size === 0);
	});

	const notAccessible = useMemo(() => isError && error?.status === 403, [isError, error]);

	const hasEditPermissionForFacility = userData.permissionsByFacility?.[facilityId]?.includes(
		'PORTAL.FLEX.ENABLE_DISABLE_STAFF'
	);

	const workforceStatusTemplate = (data) => {
		let cellClasses = 'column-status';
		if (data.worker_status === 'pending' || data.worker_status?.includes('app invite sent')) {
			cellClasses += ' cell-italic';
		} else if (['terminated', 'deactivated', 'disabled'].includes(data.worker_status)) {
			cellClasses += ' cell-warning';
		}
		return <span className={cellClasses}>{data.worker_status}</span>;
	};
	const locationStatusTemplate = (data) => {
		if (['deactivated', 'terminated'].includes(data.worker_status)) {
			return <span className="cell-warning">-</span>;
		}
		if (data.is_location_enabled) {
			return 'Enabled';
		}
		return <span className="cell-warning">Disabled</span>;
	};

	const employeeIdTemplate = (data) => data.employee_id_value || '-';

	const onUpdate = (action, professional) => {
		if (professional) {
			setSelectedAction(action);
			setSelectedProfessionals([professional]);
			setShowConfirmationModal(true);
		} else if (selectedProfessionals.length > 1) {
			setShowBulkConfirmationModal(true);
		} else {
			setSelectedAction(selectedBulkAction);
			setShowConfirmationModal(true);
		}
	};

	const handleSingleStatusChangeAction = async () => {
		try {
			await updateFlexFacilityProfessionalsStatus({
				managementCompanyId,
				facilityId,
				caregiverId: selectedProfessionals[0].id,
				body: {
					isCaregiverDisabled: selectedAction === 'DISABLE',
					shouldCancelUpcoming: !!selectedProfessionals[0].upcoming_shifts
				}
			}).unwrap();
			const statusText = `${selectedAction.toLowerCase()}d`;
			const message = `${selectedProfessionals[0]?.name} has been ${statusText} at ${facility.label}.`;
			dispatch(showNotification('success', message));

			setUpdateAnimation(true);
			setSelectedAction(null);
			setShowConfirmationModal(false);
			setSelectedBulkAction(null);
			setBulkUpdateActive(false);

			timer.current = setTimeout(() => {
				setUpdateAnimation(false);
				setSelectedProfessionals([]);
			}, 2700);
		} catch (err) {
			setSelectedAction(null);
			setUpdateAnimation(false);
			setShowConfirmationModal(false);
			setSelectedProfessionals([]);
			setBulkUpdateActive(false);
			setSelectedBulkAction(null);
			const fallbackMessage = `There was an issue completing this action. Please try again.`;
			dispatch(showNotification('error', utils.parseRTKQueryValidationError(err, fallbackMessage)));
		} finally {
			setShowBulkMenu(true);
		}
	};
	const handleBulkStatusChangeAction = async () => {
		try {
			await bulkUpdateFlexFacilityProfessionalsStatusMutation({
				managementCompanyId,
				facilityId,
				body: {
					caregiverIds: selectedProfessionals.map((selectedProfessional) => selectedProfessional.id),
					status: selectedBulkAction === 'DISABLE' ? 'DISABLED' : 'ENABLED'
				}
			}).unwrap();
			const message = `${selectedProfessionals.length} professional(s) have been ${
				selectedBulkAction === 'DISABLE' ? 'disabled' : 'enabled'
			} at this location.`;
			dispatch(showNotification('success', message));

			setUpdateAnimation(true);
			setBulkUpdateActive(false);
			setSelectedBulkAction(null);
			setShowBulkConfirmationModal(false);
			setShowBulkMenu(true);

			timer.current = setTimeout(() => {
				setUpdateAnimation(false);
				setSelectedProfessionals([]);
			}, 2700);
		} catch (err) {
			const caregiversWithUpcomingShifts = new Set(
				utils.getExtraDataFromRTKQueryValidationError(err, 'caregiversWithUpcomingShifts')
			);
			if (caregiversWithUpcomingShifts.size) {
				const updatedSelectedProfessionals = selectedProfessionals.filter(
					(selectedProfessional) => !caregiversWithUpcomingShifts.has(selectedProfessional.id)
				);
				setSelectedProfessionals(updatedSelectedProfessionals);
				dispatch(
					showNotification(
						'error',
						`${caregiversWithUpcomingShifts.size} professional(s) could no longer be bulk disabled and were removed from your selection. Please try again.`
					)
				);
			} else {
				setSelectedProfessionals([]);
				setSelectedBulkAction(null);
				const fallbackMessage = `There was an issue completing this action. Please try again.`;
				dispatch(showNotification('error', utils.parseRTKQueryValidationError(err, fallbackMessage)));
			}
			setUpdateAnimation(false);
			setShowBulkConfirmationModal(false);
		}
	};

	const onCancelConfirmation = () => {
		if (bulkUpdateActive) {
			setShowBulkConfirmationModal(false);
		} else {
			setSelectedAction(null);
		}

		if (showConfirmationModal) {
			setSelectedProfessionals([]);
			setShowConfirmationModal(false);
		}
	};

	const cancelBulkUpdate = () => {
		setBulkUpdateActive(false);
		setShowBulkMenu(true);
		setSelectedBulkAction(null);
		setSelectedProfessionals([]);
	};

	const rowClass = (data) => ({
		'disabled-status-row': data.worker_status === 'disabled',
		'strikethrough-row': ['deactivated', 'terminated'].includes(data.worker_status),
		'highlight-updated-row': updateAnimation && selectedProfessionals.find((item) => item.id === data.id)
	});

	const isRowSelectable = ({ data }) => {
		if (
			bulkUpdateActive &&
			hasEditPermissionForFacility &&
			!['deactivated', 'terminated'].includes(data.worker_status)
		) {
			if (selectedBulkAction === 'DISABLE') {
				return !data.upcoming_shifts;
			}
			return !!selectedBulkAction;
		}
		return false;
	};

	const actionsTemplate = (item) => {
		if (!hasEditPermissionForFacility || ['terminated', 'deactivated'].includes(item.worker_status)) return '';
		return (
			<div className="action-menu-container">
				<Dropdown>
					<Dropdown.Toggle
						className={classNames('action-menu', {
							disabled: (bulkUpdateActive && selectedBulkAction) || loadingUpdate || updateAnimation
						})}
					>
						<img src={KebobIcon} alt="Show More" className="action-menu-icon" />
					</Dropdown.Toggle>
					<Dropdown.Menu>
						<Dropdown.Item
							className="action-menu-item"
							onClick={() => onUpdate(!item.is_location_enabled ? 'ENABLE' : 'DISABLE', item)}
						>
							{item.is_location_enabled ? 'Disable' : 'Enable'}
						</Dropdown.Item>
					</Dropdown.Menu>
				</Dropdown>
			</div>
		);
	};

	const enabledProfessionalsCount = professionals?.filter(
		(item) => item.is_location_enabled && item.employee_status === 'enabled' && item.worker_status === 'enabled'
	).length;

	const handleSelectionChange = (event) => {
		if (bulkUpdateActive) {
			setSelectedProfessionals(event.value);
		}
	};

	const onSearch = (value) => {
		setFilters((prev) => ({
			...prev,
			global: { ...(prev.global || { matchMode: FilterMatchMode.CONTAINS }), value }
		}));
	};
	const downloadFilename = `${facility.label}_Professionals List_${moment().format('YYYY-MM-DD_hh-mm')}`;

	const exportCSV = () => {
		dataTableRef.current.exportCSV();
	};

	return (
		<div className="flex-facility-professionals">
			{searchBoxTargetDiv &&
				createPortal(
					<TextSearchComponent
						placeholder="Search for..."
						className="search-container"
						searchValue={searchTerm}
						setSearchValue={onSearch}
					/>,
					searchBoxTargetDiv
				)}
			{downloadButtonTargetDiv &&
				createPortal(
					<IsAuthorizedForManagementCompany
						exactPermission={PERMISSION.WORKFORCE_DOWNLOAD_LOCATIONS}
						managementCompanyId={managementCompanyId}
					>
						<Button
							type="button"
							className="btn-download"
							label="Download report"
							outlined
							onClick={exportCSV}
							data-pr-tooltip="Download"
							// eslint-disable-next-line react/no-unstable-nested-components
							icon={() => <img src={DownloadIcon} alt="Download" />}
						/>
					</IsAuthorizedForManagementCompany>,
					downloadButtonTargetDiv
				)}
			<div className="toolbar">
				<div className="toolbar-item">
					<span>
						<b>
							{enabledProfessionalsCount} of {professionals?.length} professionals
						</b>{' '}
						are enabled and can work at this location.
					</span>
				</div>
				<div className="toolbar-item">
					<div className="filter-container">
						{filterCount > 0 && (
							<button type="button" onClick={handleClearFilters} className="filter-btn clear">
								Clear filters
							</button>
						)}
						<button
							type="button"
							onClick={() => setIsFiltersSectionOpen(!isFiltersSectionOpen)}
							className={classNames('filter-btn-container', 'filter-btn', { noFilterCount: filterCount === 0 })}
						>
							<img src={filterIcon} alt="filters" />
							<div className="filter-text">Filters</div>
							{filterCount > 0 && <div className="filter-number">{filterCount}</div>}
							<img
								src={filterArrow}
								alt="expand"
								className={classNames('arrow-icon', { down: isFiltersSectionOpen })}
							/>
						</button>
					</div>
					{hasEditPermissionForFacility && (
						<>
							<div className="vertical-ruler" />
							{!bulkUpdateActive ? (
								<Button
									disabled={notAccessible || loadingUpdate || updateAnimation}
									className="btn actions-button"
									onClick={() => setBulkUpdateActive(true)}
								>
									{notAccessible || loadingUpdate || updateAnimation ? (
										<img src={BulkActionWhiteIcon} alt="Bulk actions" />
									) : (
										<img src={BulkActionIcon} alt="Bulk actions" />
									)}
									<div className="button-label">Bulk actions</div>
									{notAccessible || loadingUpdate || updateAnimation ? (
										<img src={arrow} alt="expand" className="arrow-icon" />
									) : (
										<img src={filterArrow} alt="expand" className="arrow-icon" />
									)}
								</Button>
							) : (
								<>
									{!selectedBulkAction && (
										<div className="d-flex">
											<Dropdown show={showBulkMenu} onToggle={(isOpen) => setShowBulkMenu(isOpen)}>
												<Dropdown.Toggle variant="primary" className="bulk-update-menu">
													<img src={BulkActionIcon} alt="Bulk actions" className="bulk-action-icon" />
													Bulk actions
													<img
														src={filterArrow}
														alt="expand"
														className={classNames('arrow-icon', { down: showBulkMenu })}
													/>
												</Dropdown.Toggle>
												<Dropdown.Menu>
													<Dropdown.Item className="action-menu-item" onClick={() => setSelectedBulkAction('ENABLE')}>
														Enable
													</Dropdown.Item>
													<Dropdown.Item className="action-menu-item" onClick={() => setSelectedBulkAction('DISABLE')}>
														Disable
													</Dropdown.Item>
												</Dropdown.Menu>
											</Dropdown>
										</div>
									)}
									{selectedBulkAction && (
										<div className="d-flex">
											<button type="button" onClick={cancelBulkUpdate} className="btn-cancel">
												Cancel
											</button>
											<Button
												disabled={notAccessible || loadingUpdate || updateAnimation}
												className={classNames('bulk-update-menu select-mode', {
													disabled: !selectedProfessionals?.length
												})}
												onClick={onUpdate}
											>
												<img src={BulkActionWhiteIcon} alt="Bulk actions" />
												<div className="button-label">
													{`${utils.capitalizeFirstLetter(selectedBulkAction)} selected (${
														selectedProfessionals.length
													})`}
												</div>
											</Button>
										</div>
									)}
								</>
							)}
						</>
					)}
				</div>
			</div>

			{isFiltersSectionOpen && (
				<div className="filters-container">
					<MultiSelectSearch
						showLongSelectionText
						className="select-filter-container select-filter-status-container"
						label="Professional status"
						options={filterOptions.worker_status}
						onApply={(options) => handleMultiSelectFiltersApply('worker_status', options)}
					/>
					<MultiSelectSearch
						showLongSelectionText
						className="select-filter-container select-filter-status-container"
						label="Location status"
						options={filterOptions.locationStatus}
						onApply={(options) => handleMultiSelectFiltersApply('locationStatus', options)}
					/>
					<MultiSelectSearch
						showLongSelectionText
						className="select-filter-container"
						label="License type"
						options={filterOptions.resourceType}
						onApply={(options) => handleMultiSelectFiltersApply('resourceType', options)}
					/>
					<MultiSelectSearch
						showLongSelectionText
						className="select-filter-container select-filter-specialty-container"
						label="Specialty"
						options={filterOptions.specialtiesIds}
						onApply={(options) => handleMultiSelectFiltersApply('specialtiesIds', options)}
					/>
				</div>
			)}

			<DataTable
				value={professionals}
				ref={dataTableRef}
				scrollHeight="calc(100vh - 360px)"
				sortMode="multiple"
				scrollable
				virtualScrollerOptions={{ itemSize: 65 }}
				size="small"
				stripedRows
				rowClassName={rowClass}
				loading={isLoading}
				emptyMessage={
					professionals?.length > 0 ? 'No professionals found. Clear or refine your search to see professionals.' : ''
				}
				removableSort
				dataKey="id"
				selection={selectedProfessionals}
				onSelectionChange={handleSelectionChange}
				selectionMode="multiple"
				isDataSelectable={isRowSelectable}
				filters={filters}
				exportFilename={downloadFilename}
				globalFilterFields={[
					'name',
					'worker_status',
					'resourceType',
					'email',
					'employee_id_value',
					'resourceTypeString',
					'specialtiesString',
					'locationStatus'
				]}
			>
				{bulkUpdateActive && !!selectedBulkAction && <Column selectionMode="multiple" style={{ width: '3%' }} />}
				<Column field="name" header="Professional" style={{ width: '20%' }} sortable />
				{professionals?.length >= 1 && professionals[0].employee_id_name && (
					<Column
						field="employee_id_value"
						header={professionals[0].employee_id_name}
						style={{ width: '10%' }}
						sortable
						body={employeeIdTemplate}
					/>
				)}
				<Column field="email" header="Email" style={{ width: '10%' }} sortable />
				<Column field="resourceTypeString" header="License type" style={{ width: '10%' }} sortable />
				<Column field="specialtiesString" header="Specialty" style={{ width: '34%' }} sortable />
				<Column
					field="worker_status"
					header="Professional status"
					style={{ width: '10%', textDecoration: 'none', opacity: 1, fontStyle: 'initial' }}
					sortable
					body={workforceStatusTemplate}
					className="status"
				/>
				<Column
					field="locationStatus"
					header="Location status"
					style={{ width: '9%', textDecoration: 'none', color: 'initial' }}
					sortable
					body={locationStatusTemplate}
					className="status"
				/>
				{hasEditPermissionForFacility && (
					<Column
						field="actions"
						header="Actions"
						style={{ maxWidth: '80px', width: '4%' }}
						headerClassName={classNames({ 'disabled-column-header': bulkUpdateActive && selectedBulkAction })}
						body={actionsTemplate}
						exportable={false}
					/>
				)}
			</DataTable>
			{showConfirmationModal && (
				<LocationsStatusUpdateConfirmation
					loading={loadingUpdate}
					onCancel={onCancelConfirmation}
					handleStatusChangeAction={handleSingleStatusChangeAction}
					selectedAction={selectedAction}
					locations={[{ facility_name: facility.label }]}
					upcomingShiftsCount={selectedProfessionals[0].upcoming_shifts}
					origin="PROFESSIONALS"
					professionalName={selectedProfessionals[0].name}
				/>
			)}
			{showBulkConfirmationModal && (
				<LocationsBulkStatusUpdateConfirmation
					onCancel={onCancelConfirmation}
					handleStatusChangeAction={handleBulkStatusChangeAction}
					selectedAction={selectedBulkAction}
					professionals={selectedProfessionals}
				/>
			)}
		</div>
	);
};

export default FlexFacilityProfessionals;
